/**
 * Subscription audit (rulebook rule 3: "Audit subscriptions monthly. Anything new
 * or increased gets reviewed.").
 *
 * Detection runs on the Worker across every transaction. It needs at least two
 * charges from the same merchant, so a household one month into hand-entering
 * data will legitimately find almost nothing — the panel says that out loud
 * instead of looking broken.
 */
import { useCallback, useState } from 'react';
import type { BudgetCategory, Subscription } from '../../shared/types';
import { api, money, parseMoney } from '../../lib/api';
import {
  Button,
  Card,
  Chip,
  Disclaimer,
  EmptyState,
  ErrorState,
  Field,
  Loading,
  PanelHeading,
  TextInput,
  errorText,
  inputClass,
  useResource,
} from './ui';

const CADENCES: Subscription['cadence'][] = ['weekly', 'monthly', 'quarterly', 'annual'];

/** What this subscription costs per month, whatever its cadence. */
function monthlyCost(sub: Subscription): number {
  switch (sub.cadence) {
    case 'weekly':
      return Math.round((sub.amount * 52) / 12);
    case 'quarterly':
      return Math.round(sub.amount / 3);
    case 'annual':
      return Math.round(sub.amount / 12);
    default:
      return sub.amount;
  }
}

function isNew(sub: Subscription): boolean {
  return sub.status === 'flagged' && sub.prev_amount === null;
}

function formatDate(iso: string | null): string {
  if (!iso) return 'unknown';
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function SubscriptionRow({
  sub,
  categories,
  others,
  onUpdate,
  onMerge,
  busy,
}: {
  sub: Subscription;
  categories: BudgetCategory[];
  others: Subscription[];
  onUpdate: (id: number, patch: Partial<Subscription>) => void;
  onMerge: (id: number, into: number) => void;
  busy: boolean;
}) {
  const category = categories.find((c) => c.id === sub.category_id);
  const [merging, setMerging] = useState(false);
  const [into, setInto] = useState('');

  return (
    <Card as="li" className={sub.status === 'flagged' ? 'border-honey/50 bg-honey-soft/40' : undefined}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-medium text-ink">{sub.merchant}</h4>
            {sub.status === 'flagged' && (
              <Chip tone="near">{isNew(sub) ? 'New — review it' : 'Price up — review it'}</Chip>
            )}
            {sub.status === 'cancelled' && <Chip tone="over">Cancelled</Chip>}
            {sub.status === 'active' && <Chip tone="ok">Keeping</Chip>}
            {category && <Chip tone="dusk">{category.name}</Chip>}
          </div>

          <p className="mt-1 text-sm text-ink-soft">
            <span className="font-medium text-ink">{money(sub.amount)}</span> {sub.cadence}
            {sub.cadence !== 'monthly' && <> · about {money(monthlyCost(sub))}/mo</>}
            {sub.prev_amount !== null && <> · was {money(sub.prev_amount)}</>}
          </p>

          <p className="mt-0.5 text-xs text-ink-faint">
            First seen {formatDate(sub.first_seen)} · last charge {formatDate(sub.last_seen)}
          </p>

          {sub.notes && <p className="mt-1.5 text-sm text-ink-soft">{sub.notes}</p>}
        </div>

        <div className="flex flex-wrap gap-2">
          {sub.status !== 'active' && (
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => onUpdate(sub.id, { status: 'active' })}
            >
              {sub.status === 'flagged' ? 'Keep it' : 'Restore'}
            </Button>
          )}
          {sub.status === 'active' && (
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => onUpdate(sub.id, { status: 'flagged' })}
            >
              Flag for review
            </Button>
          )}
          {others.length > 0 && (
            <Button
              variant="ghost"
              disabled={busy}
              aria-expanded={merging}
              onClick={() => setMerging((v) => !v)}
            >
              {merging ? 'Close' : 'Duplicate?'}
            </Button>
          )}
          {sub.status !== 'cancelled' && (
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => onUpdate(sub.id, { status: 'cancelled' })}
            >
              Mark cancelled
            </Button>
          )}
        </div>
      </div>

      {merging && others.length > 0 && (
        <form
          className="mt-3 flex flex-wrap items-end gap-2 border-t border-border pt-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (into) onMerge(sub.id, Number(into));
          }}
        >
          <Field
            label={`“${sub.merchant}” is the same subscription as…`}
            hint="They become one row. The earliest first charge, the latest amount, and both names' history are kept."
            className="min-w-56 flex-1"
          >
            <select
              value={into}
              onChange={(e) => setInto(e.target.value)}
              className={inputClass}
              required
            >
              <option value="">Pick the one to keep</option>
              {others.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.merchant} — {money(o.amount)} {o.cadence}
                </option>
              ))}
            </select>
          </Field>
          <Button type="submit" variant="primary" disabled={busy || !into}>
            Merge
          </Button>
        </form>
      )}
    </Card>
  );
}

export default function SubscriptionsPanel() {
  const loadSubs = useCallback(() => api.get<Subscription[]>('/insights/subscriptions'), []);
  const { data, loading, error, reload, setData } = useResource(loadSubs, []);

  // Category names are a nicety. If the categories route is unavailable we still
  // render the audit rather than failing the whole panel.
  const loadCats = useCallback(
    () => api.get<BudgetCategory[]>('/categories').catch(() => [] as BudgetCategory[]),
    []
  );
  const { data: categories } = useResource(loadCats, []);

  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const subs = data ?? [];
  const cats = categories ?? [];

  const tracked = subs.filter((s) => s.status !== 'cancelled');
  const flagged = subs.filter((s) => s.status === 'flagged');
  const burn = tracked.reduce((sum, s) => sum + monthlyCost(s), 0);

  async function runDetect() {
    setBusy(true);
    setActionError(null);
    setMessage(null);
    try {
      const before = new Set(subs.map((s) => s.id));
      const next = await api.post<Subscription[]>('/insights/subscriptions/detect');
      setData(next);
      const found = next.filter((s) => !before.has(s.id)).length;
      const flags = next.filter((s) => s.status === 'flagged').length;
      setMessage(
        found > 0
          ? `Scan complete — ${found} new ${found === 1 ? 'subscription' : 'subscriptions'} found. ${flags} waiting for review.`
          : next.length > 0
            ? `Scan complete — no new recurring merchants. ${flags} waiting for review.`
            : 'Scan complete — nothing recurring yet. A merchant needs at least two charges on a regular cadence before it counts.'
      );
    } catch (err) {
      setActionError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function update(id: number, patch: Partial<Subscription>) {
    setBusy(true);
    setActionError(null);
    try {
      const updated = await api.put<Subscription>(`/insights/subscriptions/${id}`, patch);
      setData(subs.map((s) => (s.id === id ? updated : s)));
    } catch (err) {
      setActionError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  /**
   * One service, two rows — a hand-typed 'Disney+' and a detected 'Disney Plus'
   * — double-counts the monthly burn. Merging folds them into one row so the
   * number is right, and detection keeps updating the survivor from then on.
   */
  async function merge(id: number, into: number) {
    const dup = subs.find((s) => s.id === id);
    const keep = subs.find((s) => s.id === into);
    if (!dup || !keep) return;
    if (
      !window.confirm(
        `Merge “${dup.merchant}” into “${keep.merchant}”? They become one subscription — the charges from both are kept, and the burn stops counting it twice.`
      )
    ) {
      return;
    }

    setBusy(true);
    setActionError(null);
    setMessage(null);
    try {
      const merged = await api.post<Subscription>(`/insights/subscriptions/${id}/merge`, { into });
      setData(subs.filter((s) => s.id !== id).map((s) => (s.id === into ? merged : s)));
      setMessage(`Merged “${dup.merchant}” into “${merged.merchant}”. The burn counts it once now.`);
    } catch (err) {
      setActionError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function add(form: HTMLFormElement) {
    const fd = new FormData(form);
    const amount = parseMoney(String(fd.get('amount') ?? ''));
    if (amount === null || amount < 0) {
      setActionError('Enter the charge amount, like 12.99.');
      return;
    }
    const categoryId = String(fd.get('category_id') ?? '');
    setBusy(true);
    setActionError(null);
    try {
      const created = await api.post<Subscription>('/insights/subscriptions', {
        merchant: String(fd.get('merchant') ?? '').trim(),
        amount,
        cadence: String(fd.get('cadence') ?? 'monthly') as Subscription['cadence'],
        category_id: categoryId ? Number(categoryId) : null,
        status: 'active',
      });
      setData([created, ...subs]);
      setAdding(false);
      form.reset();
      setMessage(`Tracking ${created.merchant}.`);
    } catch (err) {
      setActionError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <PanelHeading
        title="Subscriptions"
        sub="The monthly audit. Anything new or price-increased gets flagged here until you say keep it or kill it."
        action={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setAdding((v) => !v)} aria-expanded={adding}>
              Add by hand
            </Button>
            <Button variant="primary" onClick={runDetect} disabled={busy || loading}>
              {busy ? 'Scanning…' : 'Run audit'}
            </Button>
          </div>
        }
      />

      {adding && (
        <Card className="mb-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void add(e.currentTarget);
            }}
            className="grid gap-3 sm:grid-cols-2"
          >
            <Field label="Merchant">
              <TextInput name="merchant" required placeholder="Netflix" />
            </Field>
            <Field label="Amount per charge" hint="Dollars, e.g. 15.49">
              <TextInput name="amount" required inputMode="decimal" placeholder="15.49" />
            </Field>
            <Field label="Cadence">
              <select name="cadence" defaultValue="monthly" className={inputClass}>
                {CADENCES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Category" hint="Optional — helps the budget tracker line up.">
              <select name="category_id" defaultValue="" className={inputClass}>
                <option value="">No category</option>
                {cats.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" variant="primary" disabled={busy}>
                Save subscription
              </Button>
              <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {message && (
        <p
          role="status"
          className="mb-4 rounded-lg border border-sage/30 bg-sage-soft px-3 py-2 text-sm text-sage"
        >
          {message}
        </p>
      )}

      {actionError && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-clay/30 bg-clay-soft px-3 py-2 text-sm text-clay"
        >
          {actionError}
        </p>
      )}

      {loading && <Loading label="Loading subscriptions" />}

      {!loading && error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && subs.length === 0 && (
        <EmptyState
          title="Nothing detected yet"
          action={
            <>
              <Button variant="primary" onClick={runDetect} disabled={busy}>
                {busy ? 'Scanning…' : 'Scan transactions now'}
              </Button>
              <Button variant="secondary" onClick={() => setAdding(true)}>
                Add one by hand
              </Button>
            </>
          }
        >
          Detection looks for the same merchant charging on a regular cadence, which takes at least two
          charges — realistically a couple of months of transactions. With one month of hand-entered
          data it will find little or nothing, and that is expected, not a bug. Add the ones you already
          know about by hand and detection will keep them updated as the charges come in.
        </EmptyState>
      )}

      {!loading && !error && subs.length > 0 && (
        <div className="space-y-4">
          <Card className="bg-surface-sunk">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <div>
                <p className="text-sm text-ink-soft">Estimated monthly burn</p>
                <p className="font-display text-3xl font-semibold text-ink">{money(burn)}</p>
                <p className="mt-1 text-xs text-ink-faint">
                  {tracked.length} tracked {tracked.length === 1 ? 'subscription' : 'subscriptions'},
                  normalized to a monthly rate. Cancelled ones are excluded.
                </p>
                {subs.length > 1 && (
                  <p className="mt-1 text-xs text-ink-faint">
                    Same service listed twice? Hit <span className="font-medium text-ink">Duplicate?</span>{' '}
                    on the row and merge it into the one you keep — otherwise the burn counts it twice.
                  </p>
                )}
              </div>
              {flagged.length > 0 && (
                <Chip tone="near">
                  {flagged.length} awaiting review
                </Chip>
              )}
            </div>
          </Card>

          {flagged.length > 0 && (
            <section>
              <h3 className="mb-2 font-display text-lg font-semibold text-ink">
                Needs review ({flagged.length})
              </h3>
              <ul className="space-y-3">
                {flagged.map((sub) => (
                  <SubscriptionRow
                    key={sub.id}
                    sub={sub}
                    categories={cats}
                    others={subs.filter((s) => s.id !== sub.id)}
                    onUpdate={update}
                    onMerge={merge}
                    busy={busy}
                  />
                ))}
              </ul>
            </section>
          )}

          <section>
            <h3 className="mb-2 font-display text-lg font-semibold text-ink">Everything tracked</h3>
            <ul className="space-y-3">
              {subs
                .filter((s) => s.status !== 'flagged')
                .map((sub) => (
                  <SubscriptionRow
                    key={sub.id}
                    sub={sub}
                    categories={cats}
                    others={subs.filter((s) => s.id !== sub.id)}
                    onUpdate={update}
                    onMerge={merge}
                    busy={busy}
                  />
                ))}
            </ul>
            {subs.filter((s) => s.status !== 'flagged').length === 0 && (
              <p className="text-sm text-ink-soft">
                Everything tracked is waiting for your review above.
              </p>
            )}
          </section>

          <Disclaimer>
            Detected subscriptions are a best guess from statement descriptions — always confirm with the
            merchant before you cancel anything.
          </Disclaimer>
        </div>
      )}
    </div>
  );
}
